// Wave 5 · T1 (stage 3) — rich-text Copy Report payload for the
// Roundtable view, plus the pure answer-presentation helpers shared
// between the live render and the clipboard payload. Both surfaces
// consume the SAME helpers so the pasted report can never say something
// the screen doesn't (error copy, grounding captions, matrix columns).
// Kept out of RoundtableView.tsx so the payload shape is unit-testable
// without rendering the view — same split as searchQaCopy.ts.
//
// RAID #42 (prototype bug, ported as a fix): the prototype's Copy
// Report ran ``escapeHtml`` over raw LLM answer text, so cited call IDs
// pasted into Docs/Word as dead 16-digit strings. Here every piece of
// LLM text — per-agent answers AND comparison takeaways — flows through
// ``callIdsToHtml`` / ``callIdsToPlainText`` (which escape everything
// OUTSIDE the anchors they emit). ``escapeHtml`` is reserved for
// non-LLM text: questions, agent names, scope labels, headings, and
// error-status copy.
//
// Correctness invariant (same as searchQaCopy.ts): each block's refMap
// is built from that block's OWN ``callRefs`` — phone ordinals are
// scoped to the question's analysed call pool, so the same call_id may
// legitimately carry different ordinals in different blocks. Never
// collapse this into one thread-wide map.
import type {
  CallRef,
  RoundtableAgentAnswer,
  RoundtableErrorKind,
} from "../lib/api";
import {
  buildCallRefMap,
  callIdsToHtml,
  callIdsToPlainText,
  escapeHtml,
  type CallRefMap,
} from "../lib/linkifyCallIds";

// ---------------------------------------------------------------------------
// Answer-presentation helpers (shared by RoundtableView's render and the
// copy payload below)
// ---------------------------------------------------------------------------

// Format a single ISO timestamp as a short calendar date (e.g.
// "Jun 6, 2026"); empty string for null / unparseable input.
function formatScopeDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// PET-659 — the earliest–latest date the calls a single agent actually
// analysed span (server truth, per agent). Empty string when neither
// bound is present (e.g. a zero-call agent).
export function formatScopeRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  const s = formatScopeDate(start);
  const e = formatScopeDate(end);
  if (!s && !e) return "";
  if (s && e) return s === e ? s : `${s} – ${e}`;
  return s || e;
}

// Honest per-agent grounding caption: ``calls_used`` is the ≤100 most
// recent calls actually analysed, ``call_count`` is every call matching
// the filters — both server truth. PET-659 appends the actual date range
// those analysed calls span ("· Jun 1 – Jun 7, 2026") when the server
// surfaced it, so the reader sees how far back each agent's window
// reached (it can differ per agent when the 100-cap bites).
export function answeredFromLabel(a: {
  calls_used: number;
  call_count: number;
  range_start?: string | null;
  range_end?: string | null;
}): string {
  const base = `answered from ${a.calls_used} of ${a.call_count} call${
    a.call_count === 1 ? "" : "s"
  }`;
  const range = formatScopeRange(a.range_start, a.range_end);
  return range ? `${base} · ${range}` : base;
}

// Per-kind user-facing copy for failed agent answers (#205 semantics:
// structured categories, never error prose inside the answer text). The
// content_filter wording reuses AssessmentRunReportCard's
// FAILURE_CATEGORY_LABEL phrasing ("Blocked by provider safety filter")
// so the same failure reads the same everywhere.
export const ROUNDTABLE_ERROR_COPY: Record<RoundtableErrorKind, string> = {
  content_filter:
    "Blocked by provider safety filter — rephrasing the question may help.",
  timeout:
    "Timed out while answering for this agent — try again, or narrow the call scope.",
  upstream:
    "The AI service failed while answering for this agent — try asking again.",
  invalid:
    "The AI returned an unusable response for this agent — try asking again.",
};

// Resolve a failed answer to its user-facing copy: known kinds get the
// category copy above; an unknown kind falls back to the server's
// ``error`` string so new categories degrade gracefully.
export function agentErrorCopy(a: RoundtableAgentAnswer): string | null {
  if (a.error == null && a.error_kind == null) return null;
  return (
    (a.error_kind && ROUNDTABLE_ERROR_COPY[a.error_kind]) ||
    a.error ||
    "This agent's answer failed — try asking again."
  );
}

// Caption shown beside an agent's name (screen badge and copy payload
// alike): the zero-call empty state reads "no matching calls", anything
// else gets the honest "answered from X of Y calls" line.
export function answerCaption(a: RoundtableAgentAnswer): string {
  return a.calls_used === 0 && a.error == null
    ? "no matching calls"
    : answeredFromLabel(a);
}

// Matrix columns: the union of agents across all blocks in
// first-appearance order — NOT the live panel, so an agent removed from
// the panel mid-thread keeps their earlier answers visible in the grid.
export function matrixColumns(
  blocks: readonly { answers: RoundtableAgentAnswer[] }[],
): { agentId: string; label: string }[] {
  const seen = new Map<string, string>();
  for (const b of blocks) {
    for (const a of b.answers) {
      if (!seen.has(a.agent_id)) {
        seen.set(a.agent_id, a.agent_name || a.agent_id);
      }
    }
  }
  return Array.from(seen.entries()).map(([agentId, label]) => ({
    agentId,
    label,
  }));
}

// ---------------------------------------------------------------------------
// Copy Report payload
// ---------------------------------------------------------------------------

// Structural subset of the view's ``QaBlock`` — only the fields the
// payload needs, so RoundtableView can pass its blocks straight through.
export type RoundtableCopyBlock = {
  question: string;
  answers: RoundtableAgentAnswer[];
  comparison: string;
  callRefs: CallRef[];
  // Scope snapshotted at ask time — mid-thread filter changes never
  // relabel earlier blocks.
  scopeLabel: string;
};

export type RoundtableCopyTab = "thread" | "matrix";

// One agent's copy body: failed answers carry their honest #205
// category copy (never a fabricated answer), and everything that came
// from the LLM goes through the linkify helpers with THIS block's
// refMap (RAID #42: never escapeHtml of raw answer text).
function agentBodyHtml(a: RoundtableAgentAnswer, refMap: CallRefMap): string {
  const errCopy = agentErrorCopy(a);
  if (errCopy) return `<em>${escapeHtml(errCopy)}</em>`;
  return callIdsToHtml(a.answer ?? "", refMap).replace(/\n/g, "<br>");
}

function agentBodyText(a: RoundtableAgentAnswer, refMap: CallRefMap): string {
  const errCopy = agentErrorCopy(a);
  if (errCopy) return errCopy;
  return callIdsToPlainText(a.answer ?? "", refMap);
}

// Build the rich-text (HTML) + plain-text clipboard payload for the
// Roundtable thread, matching the ACTIVE tab: Thread mirrors the
// stacked question → comparison → per-agent structure; Matrix emits a
// real HTML table (questions × agents plus the full-width comparison
// row) so a Google Docs / Word paste keeps the grid. ``panel`` carries
// the current panelists' display labels for the report header (falls
// back to the union of answering agents if the panel was emptied
// mid-session); ``scopeLabel`` is the CURRENT scope — each block also
// carries its own ask-time snapshot.
export function buildRoundtableCopyPayload(
  blocks: readonly RoundtableCopyBlock[],
  panel: readonly string[],
  activeTab: RoundtableCopyTab,
  scopeLabel: string,
): { html: string; text: string } {
  const panelists =
    panel.length > 0 ? panel : matrixColumns(blocks).map((c) => c.label);
  const headerLine = `Panel: ${panelists.join(", ")} · Scope: ${scopeLabel}`;

  const htmlParts: string[] = [
    "<h2>Roundtable report</h2>",
    `<p>${escapeHtml(headerLine)}</p>`,
  ];
  const textParts: string[] = ["Roundtable report", headerLine, ""];

  if (activeTab === "thread") {
    for (const block of blocks) {
      // Per-block refMap — see the module invariant above.
      const refMap = buildCallRefMap(block.callRefs);
      htmlParts.push(`<h3>Q: ${escapeHtml(block.question)}</h3>`);
      htmlParts.push(`<p><em>Scope: ${escapeHtml(block.scopeLabel)}</em></p>`);
      htmlParts.push(
        `<p><strong>Comparison takeaway:</strong> ${callIdsToHtml(
          block.comparison,
          refMap,
        ).replace(/\n/g, "<br>")}</p>`,
      );
      textParts.push(`Q: ${block.question}`);
      textParts.push(`Scope: ${block.scopeLabel}`);
      textParts.push(
        `Comparison takeaway: ${callIdsToPlainText(block.comparison, refMap)}`,
      );
      textParts.push("");
      for (const a of block.answers) {
        const name = a.agent_name || a.agent_id;
        htmlParts.push(
          `<p><strong>${escapeHtml(name)}</strong> (${escapeHtml(
            answerCaption(a),
          )})<br>${agentBodyHtml(a, refMap)}</p>`,
        );
        textParts.push(`${name} (${answerCaption(a)}):`);
        textParts.push(agentBodyText(a, refMap));
        textParts.push("");
      }
    }
  } else {
    const cols = matrixColumns(blocks);
    const tableParts: string[] = [
      // Presentational table attributes (not CSS) — they survive the
      // clipboard round-trip into Google Docs / Word, where stylesheets
      // are stripped.
      '<table border="1" cellpadding="6" cellspacing="0">',
      "<thead><tr><th>Question</th>",
    ];
    for (const c of cols) tableParts.push(`<th>${escapeHtml(c.label)}</th>`);
    tableParts.push("</tr></thead><tbody>");
    for (const block of blocks) {
      // Per-block refMap — see the module invariant above.
      const refMap = buildCallRefMap(block.callRefs);
      tableParts.push(
        `<tr><th>${escapeHtml(block.question)}<br><em>${escapeHtml(
          block.scopeLabel,
        )}</em></th>`,
      );
      textParts.push(`Q: ${block.question} (${block.scopeLabel})`);
      for (const c of cols) {
        const ans = block.answers.find((x) => x.agent_id === c.agentId);
        tableParts.push(
          ans
            ? `<td><em>${escapeHtml(answerCaption(ans))}</em><br>${agentBodyHtml(
                ans,
                refMap,
              )}</td>`
            : // Agent wasn't on the panel for this question.
              "<td>—</td>",
        );
        textParts.push(
          ans
            ? `- ${c.label} (${answerCaption(ans)}): ${agentBodyText(ans, refMap)}`
            : `- ${c.label}: —`,
        );
      }
      tableParts.push("</tr>");
      tableParts.push(
        `<tr><td colspan="${cols.length + 1}"><strong>Comparison:</strong> ${callIdsToHtml(
          block.comparison,
          refMap,
        ).replace(/\n/g, "<br>")}</td></tr>`,
      );
      textParts.push(
        `Comparison: ${callIdsToPlainText(block.comparison, refMap)}`,
      );
      textParts.push("");
    }
    tableParts.push("</tbody></table>");
    htmlParts.push(tableParts.join(""));
  }

  const html = `<!doctype html><html><body>${htmlParts.join("")}</body></html>`;
  const text =
    textParts.join("\n").replace(/\n{3,}/g, "\n\n").trim() + "\n";
  return { html, text };
}
